import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnChanges } from "@angular/core";
import { UIRouterGlobals } from "@uirouter/core";
import { finalize } from "rxjs/operators";
import { AirbrakeService } from "../../../airbrake/airbrake.service";
import { TicksAgoService } from "../../../chart/ticks-ago.service";
import { CSSColorService } from "../../../css-color/css-color.service";
import { PerformanceBreakdownService } from "../../../models/performance-breakdown.service";
import { ResponseTypeService } from "../../../models/response-type.service";
import { RoundTimeService } from "../../../round-time/round-time.service";
import { TimeDurAgoService } from "../../../time-dur-ago/time-dur-ago.service";
import { sum } from "../../../utils/sum";

@Component({
  selector: "ab-performance-breakdown",
  templateUrl: "./performance-breakdown.component.html",
})
export class PerformanceBreakdownComponent implements OnChanges {
  @Input() public route: any;
  @Input() public dur: string;
  @Input() public lastN: number;

  public performanceBreakdowns: any[];
  public responseTime: number;
  public responseTimeList: number[];
  public responseTypes: any[];
  public loading: boolean;
  public currentResponseType: string;
  public hoveredItem: number;

  private COLOR_MAP = [
    this.cssColorService.getColor("--ab-blue-600"),
    this.cssColorService.getColor("--ab-green-600"),
    this.cssColorService.getColor("--ab-orange-600"),
    this.cssColorService.getColor("--ab-red-600"),
    this.cssColorService.getColor("--ab-blue-300"),
    this.cssColorService.getColor("--ab-green-300"),
    this.cssColorService.getColor("--ab-orange-300"),
    this.cssColorService.getColor("--ab-red-300"),
    this.cssColorService.getColor("--ab-blue-800"),
    this.cssColorService.getColor("--ab-red-800"),
  ];

  constructor(
    private $uiRouterGlobals: UIRouterGlobals,
    private airbrakeService: AirbrakeService,
    private cssColorService: CSSColorService,
    private performanceBreakdownService: PerformanceBreakdownService,
    private responseTypeService: ResponseTypeService,
    private roundTimeService: RoundTimeService,
    private ticksAgoService: TicksAgoService,
    private timeDurAgoService: TimeDurAgoService,
  ) {}

  public ngOnChanges() {
    this.responseTime = 0;
    this.responseTimeList = [];
    this.performanceBreakdowns = [];
    this.responseTypes = [];
    this.loading = true;

    const params = {
      projectId: this.route.projectId,
      routeId: this.route.routeId,
      time__gte: this.roundTimeService.timestampFor(
        this.timeDurAgoService.timeFor(this.dur, this.lastN),
        this.dur,
      ),
    };

    this.responseTypeService
      .get(params)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe(
        (data: any) => {
          this.responseTypes = data.types.map((t: any) => t.type);
          if (this.responseTypes.length === 0) {
            return;
          }
          const responseType = this.findOrFirst(
            this.$uiRouterGlobals.params.responseType,
            this.responseTypes,
          );
          this.currentResponseType = this.normalizeResponseType(responseType);

          this.queryPerformanceBreakdowns(responseType);
        },
        (error: HttpErrorResponse) => {
          this.catchError("ResponseType")(error);
        },
      );
  }

  public responseTypeTrackBy(i: number, _responseType: any) {
    return i;
  }

  public performanceBreakdownTrackBy(_i: number, breakdown: any) {
    return breakdown.name;
  }

  public normalizeResponseType(responseType: string): string {
    switch (responseType) {
      case "":
        return "[empty]";
      default:
        return responseType;
    }
  }

  public hoverIn(index: number) {
    this.hoveredItem = index;
  }

  public hoverOut() {
    this.hoveredItem = null;
  }

  public filterZeroPercentage(breakdowns: any) {
    return breakdowns.filter((b: any) => b.percentage > 0);
  }

  private catchError(modelName: any) {
    return (error: Error | HttpErrorResponse) => {
      if (error instanceof Error) {
        this.airbrakeService.notifier.notify(error);
      } else {
        this.airbrakeService.notifier.notify({
          error: new Error(`Couldn't fetch ${modelName}`),
          params: error,
        });
      }
    };
  }

  private normalizeGroupName(groupName: string): string {
    switch (groupName) {
      case "db":
        return "Database";
      default:
        return groupName;
    }
  }

  private normalizePercentages() {
    const percentages = this.performanceBreakdowns.map((b: any) => {
      return {
        name: b.name,
        percentage: b.percentage,
        roundedPercentage: Math.floor(b.percentage),
      };
    });
    const total: number = percentages.reduce((acc: number, p: any) => {
      return acc + p.roundedPercentage;
    }, 0);
    const diff = 100 - total;

    percentages.sort((a: any, b: any) => {
      if (Math.floor(a) - a > Math.floor(b) - b) {
        return 1;
      } else if (Math.floor(a) - a < Math.floor(b) - b) {
        return -1;
      } else {
        return 0;
      }
    });

    const finalPercentages = percentages.map((a: any, i: number) => {
      return {
        name: a.name,
        percentage:
          i < diff ? Math.floor(a.percentage) + 1 : Math.floor(a.percentage),
      };
    });

    this.performanceBreakdowns.forEach((b: any) => {
      b.percentage = finalPercentages.find(
        (p: any) => p.name === b.name,
      ).percentage;
    });
  }

  private setPerformanceBreakdowns(data: any) {
    const groups = data.groups;
    this.performanceBreakdowns = Object.keys(groups).map((groupName: any) => {
      const stats = groups[groupName];
      return {
        name: this.normalizeGroupName(groupName),
        percentage: (this.average(stats.mean) / this.responseTime) * 100,
        timing: this.average(stats.mean),
        ...stats,
      };
    });
  }

  private addOtherPerformanceBreakdown() {
    let knownPercentage = 0;
    let knownTiming = 0;

    this.performanceBreakdowns.forEach((group: any) => {
      knownPercentage += group.percentage;
      knownTiming += group.timing;
    });

    if (knownPercentage < 100) {
      this.performanceBreakdowns.push({
        name: "other",
        percentage: 100 - knownPercentage,
        timing: this.responseTime - knownTiming,
      });
    }
  }

  private sortBreakdowns() {
    this.performanceBreakdowns.sort((a: any, b: any) => {
      if (a.timing > b.timing && a.percentage > b.percentage) {
        return -1;
      } else if (a.timing < b.timing && a.percentage < b.percentage) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  private addColors() {
    this.performanceBreakdowns.forEach((b: any, i: number) => {
      if (b.name === "other") {
        b.color = this.cssColorService.getColor("--ab-grey-800");
      } else {
        b.color = this.COLOR_MAP[i % this.COLOR_MAP.length];
      }
    });
  }

  private average(array: number[]): number {
    return sum(array) / array.length;
  }

  private normalizeStat(stat: any, time: string[], ticks: Date[]) {
    return ticks.map((tick: Date) => {
      let value = 0;

      time.forEach((timestamp: string, i: number) => {
        if (new Date(timestamp).getTime() === tick.getTime()) {
          value = stat.count[i];
        }
      });

      return value;
    });
  }

  private setResponseTime(data: any) {
    this.responseTime = this.average(data.total.mean);
    this.responseTimeList = this.normalizeStat(
      data.total,
      data.time,
      this.ticksAgoService.ticksFor(this.dur, this.lastN),
    );

    return data;
  }

  private queryPerformanceBreakdowns(responseType: string) {
    const period = (() => {
      switch (this.dur.split("/")[1]) {
        case "1d":
          return "day";
        case "1h":
          return "hour";
        case "1m":
          return "minute";
      }
    })();

    const params = {
      projectId: this.route.projectId,
      routeId: this.route.routeId,
      time__gte: this.roundTimeService.timestampFor(
        this.timeDurAgoService.timeFor(this.dur, this.lastN),
        this.dur,
      ),
      period,
      response_type: responseType,
      with_normalized_groups: true,
    };

    return this.performanceBreakdownService.query(params).subscribe(
      (data: any) => {
        this.setResponseTime(data);
        this.setPerformanceBreakdowns(data);
        this.normalizePercentages();
        this.addOtherPerformanceBreakdown();
        this.sortBreakdowns();
        this.addColors();
      },
      (error: HttpErrorResponse) => {
        this.catchError("PerformanceBreakdown")(error);
      },
    );
  }

  private findOrFirst(needle: string, haystack: string[]): string {
    return haystack.indexOf(needle) > -1 ? needle : haystack[0];
  }
}
